import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  StaticFieldForm,
  StaticFieldFormData,
} from "./fieldForms/StaticFieldForm";
import {
  ComputedFieldForm,
  ComputedFieldFormData,
} from "./fieldForms/ComputedFieldForm";
import {
  AttributeTableFieldForm,
  AttributeTableFieldFormData,
} from "./fieldForms/AttributeTableFieldForm";
import {
  SampleTableFieldForm,
  SampleTableFieldFormData,
} from "./fieldForms/SampleTableFieldForm";
import {
  DetailTestingTableFieldForm,
  DetailTestingTableFieldFormData,
} from "./fieldForms/DetailTestingTableFieldForm";
import {
  CitationAssetSheetFieldForm,
  CitationAssetSheetFieldFormData,
} from "./fieldForms/CitationAssetSheetFieldForm";
import {
  ComputedTableFieldForm,
  ComputedTableFieldFormData,
} from "./fieldForms/ComputedTableFieldForm";
import {
  DraftTemplateField,
  TemplateType,
  SOURCES_BY_TEMPLATE_TYPE,
  getFieldTypeLabel,
} from "../../models/workPaperTemplate";

interface FieldEditorDialogProps {
  field: DraftTemplateField | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (fieldId: string, updates: Partial<DraftTemplateField>) => void;
  readOnly?: boolean;
  templateType: TemplateType;
}

export function FieldEditorDialog({
  field,
  open,
  onOpenChange,
  onUpdate,
  readOnly = false,
  templateType,
}: FieldEditorDialogProps) {
  const [fieldName, setFieldName] = useState<string>("");
  const [staticFieldData, setStaticFieldData] =
    useState<StaticFieldFormData | null>(null);
  const [computedFieldData, setComputedFieldData] =
    useState<ComputedFieldFormData | null>(null);
  const [attributeTableData, setAttributeTableData] =
    useState<AttributeTableFieldFormData | null>(null);
  const [sampleTableData, setSampleTableData] =
    useState<SampleTableFieldFormData | null>(null);
  const [detailTestingTableData, setDetailTestingTableData] =
    useState<DetailTestingTableFieldFormData | null>(null);
  const [citationAssetSheetData, setCitationAssetSheetData] =
    useState<CitationAssetSheetFieldFormData | null>(null);
  const [computedTableData, setComputedTableData] =
    useState<ComputedTableFieldFormData | null>(null);

  useEffect(() => {
    if (!field) {
      return;
    }

    // Initialize field name
    setFieldName(field.name);

    // Initialize form data based on field type
    switch (field.fieldType) {
      case "static":
        if (field.staticField) {
          setStaticFieldData({
            sheetName: field.staticField.sheetName,
            cellRow: field.staticField.cellAddress.row,
            cellColumn: field.staticField.cellAddress.column,
            source: field.staticField.source,
            sourceSelector: field.staticField.sourceSelector,
          });
        }
        break;

      case "dynamic":
        if (field.computedField) {
          setComputedFieldData({
            sheetName: field.computedField.sheetName,
            cellRow: field.computedField.cellAddress.row,
            cellColumn: field.computedField.cellAddress.column,
            context: field.computedField.context,
            instructions: field.computedField.instructions,
          });
        }
        break;

      case "attribute_summary_table":
        if (field.attributeSummaryTable) {
          setAttributeTableData({
            ...field.attributeSummaryTable,
            attributeOutcomeDescriptionColumn:
              field.attributeSummaryTable.attributeOutcomeDescriptionColumn ??
              null,
            descriptionInstructions:
              field.attributeSummaryTable.descriptionInstructions ?? null,
            evidenceSummaryColumn:
              field.attributeSummaryTable.evidenceSummaryColumn ?? null,
            concatEvidenceWithDescription:
              field.attributeSummaryTable.concatEvidenceWithDescription ??
              false,
          });
        }
        break;

      case "sample_summary_table":
        if (field.sampleSummaryTable) {
          setSampleTableData(field.sampleSummaryTable);
        }
        break;

      case "detail_testing_table":
        if (field.detailTestingTable) {
          // Derive sheetNamingMode from existing data
          const mode = field.detailTestingTable.sheetName ? "single" : "multi";
          setDetailTestingTableData({
            sheetNamingMode: mode,
            sheetNamePrefix: field.detailTestingTable.sheetNamePrefix || "",
            sheetName: field.detailTestingTable.sheetName ?? undefined,
            sheetNameTemplate: field.detailTestingTable.sheetNameTemplate,
            startRow: field.detailTestingTable.startRow,
            headerRow: field.detailTestingTable.headerRow,
            columnStart: field.detailTestingTable.columnStart,
            includeSampleAttributes:
              field.detailTestingTable.includeSampleAttributes,
            includeProcedureIds: field.detailTestingTable.includeProcedureIds,
            colors: field.detailTestingTable.colors,
          });
        }
        break;

      case "citation_asset_sheet":
        if (field.citationAssetSheet) {
          setCitationAssetSheetData({
            sheetNamePrefix: field.citationAssetSheet.sheetNamePrefix,
            sheetNameTemplate: field.citationAssetSheet.sheetNameTemplate,
            organization: field.citationAssetSheet.organization,
            startRow: field.citationAssetSheet.startRow,
            imagesPerRow: field.citationAssetSheet.imagesPerRow,
            includeAnnotations: field.citationAssetSheet.includeAnnotations,
            includeNonAttributeTests:
              field.citationAssetSheet.includeNonAttributeTests,
            includeProcedureIds: field.citationAssetSheet.includeProcedureIds,
            truncateEvalColumnStepsTaken:
              field.citationAssetSheet.truncateEvalColumnStepsTaken,
          });
        }
        break;

      case "computed_table":
        if (field.computedTableField) {
          setComputedTableData({
            sheetName: field.computedTableField.sheetName,
            startRow: field.computedTableField.startRow,
            headerRow: field.computedTableField.headerRow,
            columnStart: field.computedTableField.columnStart,
            context: field.computedTableField.context,
            instructions: field.computedTableField.instructions,
            columns: field.computedTableField.columns.map((col) => ({
              id: col.id,
              name: col.name,
              dataType: col.dataType,
              instructions: col.instructions ?? null,
              columnOrder: col.columnOrder,
            })),
          });
        }
        break;
    }
  }, [field]);

  const handleUpdate = () => {
    if (!field) {
      return;
    }

    let updates: Partial<DraftTemplateField> = {
      name: fieldName,
    };

    switch (field.fieldType) {
      case "static":
        if (staticFieldData) {
          updates = {
            ...updates,
            staticField: {
              id: field.staticField?.id || "",
              sheetName: staticFieldData.sheetName,
              cellAddress: {
                row: staticFieldData.cellRow,
                column: staticFieldData.cellColumn,
              },
              source: staticFieldData.source,
              sourceSelector: staticFieldData.sourceSelector,
            },
          };
        }
        break;

      case "dynamic":
        if (computedFieldData) {
          updates = {
            ...updates,
            computedField: {
              id: field.computedField?.id || "",
              sheetName: computedFieldData.sheetName,
              cellAddress: {
                row: computedFieldData.cellRow,
                column: computedFieldData.cellColumn,
              },
              context: computedFieldData.context,
              instructions: computedFieldData.instructions,
            },
          };
        }
        break;

      case "attribute_summary_table":
        if (attributeTableData) {
          updates = {
            ...updates,
            attributeSummaryTable: attributeTableData,
          };
        }
        break;

      case "sample_summary_table":
        if (sampleTableData) {
          updates = {
            ...updates,
            sampleSummaryTable: sampleTableData,
          };
        }
        break;

      case "detail_testing_table":
        if (detailTestingTableData) {
          updates = {
            ...updates,
            detailTestingTable: {
              // XOR: only include the active mode's field
              sheetName:
                detailTestingTableData.sheetNamingMode === "single"
                  ? detailTestingTableData.sheetName
                  : undefined,
              sheetNamePrefix:
                detailTestingTableData.sheetNamingMode === "multi"
                  ? detailTestingTableData.sheetNamePrefix
                  : undefined,
              sheetNameTemplate:
                detailTestingTableData.sheetNameTemplate ??
                "{prefix}_{procedure_name}",
              startRow: detailTestingTableData.startRow ?? 2,
              headerRow: detailTestingTableData.headerRow ?? 1,
              columnStart: detailTestingTableData.columnStart ?? "A",
              includeSampleAttributes:
                detailTestingTableData.includeSampleAttributes ?? true,
              includeProcedureIds: detailTestingTableData.includeProcedureIds,
              colors: detailTestingTableData.colors ?? null,
            },
          };
        }
        break;

      case "citation_asset_sheet":
        if (citationAssetSheetData) {
          updates = {
            ...updates,
            citationAssetSheet: {
              sheetNamePrefix: citationAssetSheetData.sheetNamePrefix,
              sheetNameTemplate:
                citationAssetSheetData.sheetNameTemplate ??
                "{prefix}_{procedure_name}",
              organization: citationAssetSheetData.organization ?? "by_sample",
              startRow: citationAssetSheetData.startRow ?? 1,
              imagesPerRow: citationAssetSheetData.imagesPerRow ?? 1,
              includeAnnotations:
                citationAssetSheetData.includeAnnotations ?? true,
              includeNonAttributeTests:
                citationAssetSheetData.includeNonAttributeTests ?? false,
              includeProcedureIds: citationAssetSheetData.includeProcedureIds,
              truncateEvalColumnStepsTaken:
                citationAssetSheetData.truncateEvalColumnStepsTaken ?? false,
            },
          };
        }
        break;

      case "computed_table":
        if (computedTableData) {
          updates = {
            ...updates,
            computedTableField: {
              id: field.computedTableField?.id || "",
              sheetName: computedTableData.sheetName,
              startRow: computedTableData.startRow ?? 2,
              headerRow: computedTableData.headerRow ?? 1,
              columnStart: computedTableData.columnStart ?? "A",
              context: computedTableData.context,
              instructions: computedTableData.instructions,
              columns: computedTableData.columns.map((col) => ({
                id: col.id,
                name: col.name,
                dataType: col.dataType,
                instructions: col.instructions,
                columnOrder: col.columnOrder,
              })),
            },
          };
        }
        break;
    }

    onUpdate(field.id, updates);
    onOpenChange(false);
  };

  if (!field) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Field</DialogTitle>
          <DialogDescription>
            {getFieldTypeLabel(field.fieldType)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="fieldName">Field Name</Label>
            <Input
              id="fieldName"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="e.g., Control Name, Test Summary, Sample Results"
              maxLength={255}
              disabled={readOnly}
            />
            <p className="text-sm text-muted-foreground">
              A descriptive name to identify this field
            </p>
          </div>

          <div className="border-t pt-4">
            {field.fieldType === "static" && staticFieldData && (
              <StaticFieldForm
                value={staticFieldData}
                onChange={setStaticFieldData}
                readOnly={readOnly}
                allowedSources={SOURCES_BY_TEMPLATE_TYPE[templateType]}
              />
            )}
            {field.fieldType === "dynamic" && computedFieldData && (
              <ComputedFieldForm
                value={computedFieldData}
                onChange={setComputedFieldData}
                readOnly={readOnly}
              />
            )}
            {field.fieldType === "attribute_summary_table" &&
              attributeTableData && (
                <AttributeTableFieldForm
                  value={attributeTableData}
                  onChange={setAttributeTableData}
                  readOnly={readOnly}
                />
              )}
            {field.fieldType === "sample_summary_table" && sampleTableData && (
              <SampleTableFieldForm
                value={sampleTableData}
                onChange={setSampleTableData}
                readOnly={readOnly}
              />
            )}
            {field.fieldType === "detail_testing_table" &&
              detailTestingTableData && (
                <DetailTestingTableFieldForm
                  value={detailTestingTableData}
                  onChange={setDetailTestingTableData}
                  readOnly={readOnly}
                />
              )}
            {field.fieldType === "citation_asset_sheet" &&
              citationAssetSheetData && (
                <CitationAssetSheetFieldForm
                  value={citationAssetSheetData}
                  onChange={setCitationAssetSheetData}
                  readOnly={readOnly}
                />
              )}
            {field.fieldType === "computed_table" && computedTableData && (
              <ComputedTableFieldForm
                value={computedTableData}
                onChange={setComputedTableData}
                readOnly={readOnly}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!readOnly && <Button onClick={handleUpdate}>Update Field</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
